import { Component, input, output, inject, LOCALE_ID, signal, computed } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Loading } from '../../../../utils/loading/loading';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { ApprovedPharmacyListItem } from '../../../../pages/admin-dashboard/admin-approved-pharmacies/approved-pharmacies.model';
import { EmptyData } from '../../../../utils/empty-data/empty-data';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { AssignEmployeeDialog } from '../../pharmacies/assign-employee-dialog/assign-employee-dialog';
import { Observable } from 'rxjs';
import { ApprovedPharmaciesService } from '../../../../services/pharmacy/approved-pharmacies.service';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { toPharmacyTableRows, toTeleSalesTableRows } from './approved-ph-table.mapper';
import { ActionInterface } from '../../../../pages/admin-dashboard/pharmacies/pharmacies-list/pharmacies-list.model';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';

export type LoadState = 'loading' | 'success' | 'error';

@Component({
  selector: 'app-approved-ph-table',
  imports: [
    DatePipe,
    DecimalPipe,
    Loading,
    HasScopeDirective,
    FormsModule,
    EmptyData,
    ErrorData,
    AssignEmployeeDialog,
    Tables,
    Pagination,
  ],
  templateUrl: './approved-ph-table.html',
  styleUrl: './approved-ph-table.scss',
  standalone: true,
})
export class ApprovedPhTable {
  private locale = inject(LOCALE_ID);
  readonly SCOPES = SCOPES;
  private approvedPharmaciesService = inject(ApprovedPharmaciesService);

  pharmacies = input<ApprovedPharmacyListItem[] | null>(null);
  view = input<'admin' | 'telesales'>('admin');
  rows = computed(() =>
    this.view() === 'telesales'
      ? toTeleSalesTableRows(this.pharmacies() ?? [], this.isArabic())
      : toPharmacyTableRows(this.pharmacies() ?? [], this.isArabic()),
  );
  state = input<LoadState>('loading');
  currentPage = input<number>(1);
  totalItems = input<number>(0);
  itemsPerPage = input<number>(25);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);

  pageChange = output<number>();
  pageSizeChange = output<number>();
  // rowClick = output<number>();
  rowClick = output<ApprovedPharmacyListItem>();
  assignSuccess = output<number>();
  followUp = output<ApprovedPharmacyListItem>();

  activeDropdownId = signal<number | null>(null);
  showAssignDialog = signal(false);
  selectedPharmacy = signal<ApprovedPharmacyListItem | null>(null);
  currentActions = signal<ActionInterface[]>([]);

  tableHeaderData = signal([
    { key: 'pharmacyNickname', label: $localize`:@@adminApprovedPhNickname:Pharmacy Nickname` },
    { key: 'pharmacyOwner', label: $localize`:@@adminApprovedPhOwner:Pharmacy Owner` },
    { key: 'assignedTo', label: $localize`:@@adminApprovedPhAssignedTo:Assigned To` },
    { key: 'representative', label: $localize`:@@adminApprovedPhRepresentative:Representative` },
    { key: 'pharmacyCredit', label: $localize`:@@adminApprovedPhCredit:Pharmacy Credit`, type: 'currency' },
    { key: 'pharmacySegment', label: $localize`:@@adminApprovedPhSegment:Pharmacy Segment` },
    { key: 'createdOnBy', label: $localize`:@@adminApprovedPhCreatedOn:Created On / By` },
    { key: 'lastModifiedOnBy', label: $localize`:@@adminApprovedPhLastModified:Last Modified On / By` },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  teleSalesHeaderData = computed(() => {
    const ar = this.isArabic();
    return [
      { key: 'pharmacyCode', label: ar ? 'كود الصيدلية' : 'Pharmacy Code' },
      { key: 'pharmacyOwnerLabel', label: ar ? 'الصيدلية / المالك' : 'Pharmacy / Owner' },
      { key: 'address', label: ar ? 'العنوان' : 'Address' },
      { key: 'phone', label: ar ? 'رقم التليفون' : 'Phone Number' },
      { key: 'representative', label: ar ? 'المندوب' : 'Representative' },
      { key: 'availableCredit', label: ar ? 'الائتمان المتاح' : 'Available Credit', type: 'currency' },
      { key: 'activityStatus', label: ar ? 'الحالة' : 'Status', type: 'activityStatus' },
    ];
  });

  headerItems = computed(() => (this.view() === 'telesales' ? this.teleSalesHeaderData() : this.tableHeaderData()));

  emptyFilterTitle = computed(() => {
    if (this.view() === 'telesales') {
      return this.isArabic() ? 'لا توجد صيدليات مطابقة لمعايير البحث المحددة.' : 'No pharmacies found matching the selected criteria.';
    }
    return this.isArabic() ? 'لا توجد صيدليات تطابق معاييرك.' : 'No pharmacies match your criteria.';
  });

  emptyContentTitle = computed(() => {
    if (this.view() === 'telesales') {
      return this.isArabic() ? 'لا توجد صيدليات مُسندة.' : 'No assigned pharmacies available.';
    }
    return this.isArabic() ? 'لا توجد صيدليات.' : 'No Pharmacies Found';
  });

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];
    if (total <= 1) return range;
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) range.push(i);
    }
    range.push(total);
    const result: number[] = [];
    let prev: number | undefined;
    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) result.push(prev + 1);
        else if (i - prev !== 1) result.push(-1);
      }
      result.push(i);
      prev = i;
    }
    return result;
  });

  onRowShift(row: Record<string, any>) {
    const pharmacy = this.pharmacies()?.find((p) => p.id === row['id']);
    if (pharmacy) this.followUp.emit(pharmacy);
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }
  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }
  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }
  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }
  onPageSizeChange() {}

  readonly autoCompleteFn = (term: string): Observable<{ list: { id: number; fullName: string }[] }> =>
    this.approvedPharmaciesService.teleSalesEmployeeAutoComplete(term);

  readonly submitFn = (pharmacyId: number, body: { teleSalesEmployeeId: number; comment?: string }): Observable<void> =>
    this.approvedPharmaciesService.assignTeleSalesEmployee(pharmacyId, {
      ...body,
      comment: body.comment ?? '',
    });

  VIEW_PHARMACY_DETAILS = signal<LookupVTO>({
    id: 6,
    titleEn: 'View Pharmacy Details',
    titleAr: 'عرض تفاصيل الصيدلية',
  });

  toggleDropdown(id?: any) {
    if (this.activeDropdownId() === id) {
      this.activeDropdownId.set(null);
    } else {
      this.activeDropdownId.set(id);
      const pharmacy = this.pharmacies()?.find((p) => p.id === id);
      this.getActions(pharmacy!);
    }
  }

  getActions(pharmacy: ApprovedPharmacyListItem) {
    switch (this.isUnassigned(pharmacy)) {
      case true:
        this.currentActions.set([
          { id: 1, titleEn: 'Assign Tele Sales', titleAr: 'تعيين موظف مبيعات' },
          this.VIEW_PHARMACY_DETAILS(),
        ]);
        return;
      case false:
        this.currentActions.set([
          { id: 2, titleEn: 'Re-assign Tele Sales', titleAr: 'إعادة تعيين موظف مبيعات' },
          this.VIEW_PHARMACY_DETAILS(),
        ]);
        return;
      default:
        return [];
    }
  }

  handleAction(action: { actionId: number; event: Event }) {
    const { actionId, event } = action;
    event?.stopPropagation();

    const pharmacy = this.pharmacies()?.find((p) => p.id === this.activeDropdownId());

    if (!pharmacy) return;

    if (actionId === 6) {
      this.onViewDetails(event, pharmacy);
    } else if (actionId === 1 || actionId === 2) {
      this.onAssign(event, pharmacy);
    }

    this.activeDropdownId.set(null);
  }

onViewDetails(event: MouseEvent | Event, pharmacy: ApprovedPharmacyListItem) {
  event.stopPropagation();
  this.rowClick.emit(pharmacy);
}

  onAssign(event: MouseEvent | Event, pharmacy: ApprovedPharmacyListItem) {
    event.stopPropagation();
    this.selectedPharmacy.set(pharmacy);
    this.showAssignDialog.set(true);
  }

  onAssignDialogClose() {
    this.showAssignDialog.set(false);
    this.selectedPharmacy.set(null);
  }

  onAssignDialogSuccess(event: { action: any; employee: { id: number; fullName: string } }) {
    const pharmacy = this.selectedPharmacy();
    if (pharmacy) this.assignSuccess.emit(pharmacy.id);
    this.onAssignDialogClose();
  }

  isUnassigned(pharmacy: ApprovedPharmacyListItem): boolean {
    return !pharmacy.teleSalesEmployee;
  }
  isArabic = computed(() => this.locale.startsWith('ar'));
  isEnglish = computed(() => this.locale.startsWith('en'));
}
